import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Loader2, Sparkles, ChevronDown, ArrowRight, Bell, FileDown, Share2,
  ShieldCheck, GraduationCap, FileText, FileSignature, DollarSign, CreditCard, Building2, Award,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import Milestones from "./progress/Milestones";
import Goals from "./progress/Goals";
import Timeline from "./progress/Timeline";
import { AiMarkdown } from "@/components/AiMarkdown";

const KPI_ICONS = { formation: Building2, credit: CreditCard, funding: DollarSign, compliance: ShieldCheck, education: GraduationCap };

function Ring({ value, label }) {
  return (
    <div className="relative w-28 h-28 shrink-0">
      <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
        <circle cx="50" cy="50" r="42" fill="none" stroke="#D4AF37" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={264} strokeDashoffset={264 - (264 * (value || 0)) / 100} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl text-white" data-testid="overall-progress">{value ?? 0}%</span>
        <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</span>
      </div>
    </div>
  );
}

export default function ProgressTracker() {
  const [data, setData] = useState(null);
  const [showFactors, setShowFactors] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [reporting, setReporting] = useState("");
  const [sharing, setSharing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/progress").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const genAi = async () => {
    setAiLoading(true);
    try { const { data } = await api.get("/progress/ai"); setAi(data.recommendations); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI unavailable"); }
    finally { setAiLoading(false); }
  };
  const report = async (kind) => {
    setReporting(kind);
    try { const { data } = await api.post("/progress/reports", { kind }); toast.success(`${data.filename} saved to Document Library`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate report"); }
    finally { setReporting(""); }
  };
  const shareReadiness = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/progress/share");
      const url = `${window.location.origin}${data.path}`;
      try { await navigator.clipboard.writeText(url); toast.success("Shareable link copied to clipboard"); }
      catch { toast.success("Link created"); }
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create link"); }
    finally { setSharing(false); }
  };

  if (!data) return <div className="flex justify-center py-24 text-[#71717A]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const k = data.kpis;
  const h = data.health;
  const kpiTiles = [
    { key: "formation", label: "Formation", val: k.formation_pct, suffix: "%" },
    { key: "credit", label: "Business credit", val: k.credit_pct, suffix: "%" },
    { key: "funding", label: "Funding readiness", val: k.funding_pct, suffix: "%" },
    { key: "compliance", label: "Compliance", val: k.compliance_pct, suffix: "%" },
    { key: "education", label: "Learning", val: k.education_pct, suffix: "%" },
  ];
  const counts = [
    { icon: FileText, label: "Documents", val: k.documents },
    { icon: FileSignature, label: "Contracts done", val: k.contracts_done },
    { icon: DollarSign, label: "Funding apps", val: k.funding_apps },
    { icon: Award, label: "Certificates", val: k.certificates },
  ];

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Grow" title="Business Command Center"
        subtitle="A real-time, database-driven view of your business journey across every module." />

      {/* Health hero */}
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 sm:p-8 mb-5 mt-6" data-testid="health-hero">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <Ring value={data.overall} label="Overall" />
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 text-[#D4AF37] mb-1"><TrendingUp className="w-5 h-5" /><span className="text-sm uppercase tracking-wider">Business Health Score</span></div>
            <div className="flex items-baseline gap-2 justify-center sm:justify-start">
              <span className="font-heading text-4xl text-white" data-testid="health-score">{h.score}</span>
              <span className="text-[#A1A1AA]">/ 100 · {h.band}</span>
            </div>
            <Progress value={h.score} className="h-2 bg-white/10 mt-3 max-w-md" />
            <div className="flex items-center gap-3 mt-3 justify-center sm:justify-start flex-wrap">
              <button onClick={() => setShowFactors((s) => !s)} data-testid="toggle-factors" className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1">Why this score <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFactors ? "rotate-180" : ""}`} /></button>
              <button onClick={() => navigate(data.next_module.route)} data-testid="next-module-btn" className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1">Next: {data.next_module.label} <ArrowRight className="w-3 h-3" /></button>
            </div>
          </div>
        </div>
        {showFactors && (
          <div className="grid sm:grid-cols-2 gap-2.5 mt-5 pt-5 border-t border-white/8" data-testid="health-factors">
            {h.factors.map((f) => (
              <div key={f.key} className="rounded-lg bg-black/20 p-3">
                <div className="flex justify-between text-xs mb-1"><span className="text-white">{f.label}</span><span className="text-[#D4AF37]">{f.score}%</span></div>
                <Progress value={f.score} className="h-1 bg-white/10" />
                <p className="text-[11px] text-[#71717A] mt-1.5">{f.explanation}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4" data-testid="kpi-grid">
        {kpiTiles.map((t) => {
          const Icon = KPI_ICONS[t.key];
          return (
            <button key={t.key} onClick={() => navigate((data.modules.find((m) => m.name.toLowerCase().includes(t.key)) || {}).route || "/dashboard")}
              data-testid={`kpi-${t.key}`} className="rounded-xl border border-white/8 bg-[#121214] p-4 text-left hover:border-white/15 transition-colors">
              <Icon className="w-4 h-4 text-[#D4AF37] mb-2" />
              <div className="font-heading text-2xl text-white" style={{ fontVariantNumeric: "tabular-nums" }}>{t.val}{t.suffix}</div>
              <div className="text-[11px] text-[#71717A] mt-0.5">{t.label}</div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="count-grid">
        {counts.map((c) => (
          <div key={c.label} className="rounded-xl border border-white/8 bg-[#121214] p-4 flex items-center gap-3">
            <c.icon className="w-5 h-5 text-[#D4AF37]" />
            <div><div className="font-heading text-xl text-white">{c.val}</div><div className="text-[11px] text-[#71717A]">{c.label}</div></div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#121214] border border-white/8 mb-5 flex-wrap h-auto">
          {[["overview", "Overview"], ["milestones", "Milestones"], ["goals", "Goals"], ["activity", "Activity"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="data-[state=active]:bg-[#D4AF37]/15 data-[state=active]:text-[#D4AF37] text-[#A1A1AA]">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="space-y-5">
          <div className="grid lg:grid-cols-2 gap-5">
            {/* Reminders */}
            <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="reminders-card">
              <h3 className="font-heading text-lg text-white flex items-center gap-2 mb-3"><Bell className="w-5 h-5 text-[#D4AF37]" /> Reminders</h3>
              {data.reminders.length === 0 ? <p className="text-sm text-[#71717A] py-4">No upcoming deadlines. You're all clear.</p> : (
                <div className="space-y-2">
                  {data.reminders.slice(0, 6).map((r, i) => (
                    <button key={i} onClick={() => navigate(r.route)} data-testid={`reminder-${i}`} className="w-full flex items-center gap-2 text-left text-xs text-[#D4D4D8] hover:text-white py-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.days < 0 ? "bg-red-400" : r.days <= 7 ? "bg-amber-400" : "bg-[#D4AF37]"}`} />
                      <span className="truncate flex-1">{r.title}</span>
                      <span className="text-[#71717A] shrink-0">{r.kind} · {r.days < 0 ? `${-r.days}d ago` : `in ${r.days}d`}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* AI insights */}
            <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#151310] p-5" data-testid="ai-insights-card">
              <h3 className="font-heading text-lg text-white flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> AI Recommendations</h3>
              {!data.features.ai ? (
                <p className="text-sm text-[#A1A1AA]">AI recommendations are available on the Growth plan and above.</p>
              ) : ai ? (
                <AiMarkdown text={ai} className="text-sm text-[#D4D4D8] leading-relaxed max-h-72 overflow-y-auto" data-testid="ai-recommendations" />
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-[#71717A] mb-3">Let the AI Advisor analyze your progress and tell you exactly what to do next.</p>
                  <Button onClick={genAi} disabled={aiLoading} data-testid="generate-ai-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
                    {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Get recommendations</>}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Module progress */}
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="modules-card">
            <h3 className="font-heading text-lg text-white mb-4">Module progress</h3>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {data.modules.map((m) => (
                <button key={m.name} onClick={() => navigate(m.route)} data-testid={`module-${m.name}`} className="text-left group">
                  <div className="flex justify-between mb-1"><span className="text-sm text-[#D4D4D8] group-hover:text-white">{m.name}</span><span className="text-sm text-[#D4AF37]">{m.progress}%</span></div>
                  <Progress value={m.progress} className="h-1.5 bg-white/5" />
                </button>
              ))}
            </div>
          </div>

          {/* Reports */}
          <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="reports-card">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <h3 className="font-heading text-lg text-white flex items-center gap-2"><FileDown className="w-5 h-5 text-[#D4AF37]" /> Export reports</h3>
              <Button onClick={shareReadiness} disabled={sharing} data-testid="share-readiness-btn" size="sm" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
                {sharing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5 mr-1.5" />}Share readiness report
              </Button>
            </div>
            <p className="text-xs text-[#71717A] mb-4">Generate a PDF report (saved to your Document Library), or share a live, secure readiness link with lenders and partners.</p>
            <div className="flex flex-wrap gap-2">
              {[["progress", "Progress"], ["milestone", "Milestone"], ["task", "Task & Goals"], ["readiness", "Readiness"], ["compliance", "Compliance"]].map(([kind, label]) => {
                const advanced = ["readiness", "compliance"].includes(kind);
                const locked = advanced && !data.features.advanced_reports;
                return (
                  <Button key={kind} onClick={() => locked ? toast.error("This report requires the Growth plan or higher.") : report(kind)} disabled={reporting === kind}
                    data-testid={`report-${kind}`} variant="outline" className="border-white/15 bg-transparent text-white rounded-full text-xs">
                    {reporting === kind ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}{label}{locked ? " 🔒" : ""}
                  </Button>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="milestones"><Milestones milestones={data.milestones} onNavigate={navigate} /></TabsContent>
        <TabsContent value="goals"><Goals /></TabsContent>
        <TabsContent value="activity"><Timeline /></TabsContent>
      </Tabs>
    </div>
  );
}
